import { createApiErrorFromResponse, readApiErrorDetail } from "@airange/shared/api";
import { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import { resolveMicroLabApiEndpoint } from "./env";
import type { MicroLabCurrentUser } from "./micro-lab-auth-api";
import { clearSession } from "./session";

type MicroLabAdminInitialState = {
  authStatus?: MicroLabAdminAuthStatus;
  currentUser?: MicroLabCurrentUser | null;
  microLabUser?: MicroLabCurrentUser | null;
  [key: string]: unknown;
};

export type MicroLabAdminAuthStatus = "authenticated" | "no_session" | "no_access";

export type OidcCallbackCompleteResult = {
  redirectPath: string;
};

export { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";

const MICRO_LAB_ADMIN_APPLICATION_KEY = "micro-lab-admin";

function authEndpoint(path: "/authorize" | "/callback" | "/bootstrap" | "/logout") {
  return resolveMicroLabApiEndpoint(`/api/auth/${MICRO_LAB_ADMIN_APPLICATION_KEY}${path}`);
}

function applicationHeaders() {
  return { "X-AIRange-Application-Key": MICRO_LAB_ADMIN_APPLICATION_KEY };
}

async function parseJson<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export function consumeOidcRedirectPath(defaultPath = "/schemes") {
  return defaultPath;
}

export async function startOidcLogin(redirectPath: string, prompt: "none" | "login" = "login", fetchImpl: typeof fetch = fetch) {
  const response = await fetchImpl(authEndpoint("/authorize"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...applicationHeaders() },
    body: JSON.stringify({ return_path: redirectPath, prompt }),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  const result = await parseJson<{ authorization_url: string }>(response);
  window.location.assign(result.authorization_url);
}

export async function completeOidcCallback(code: string, state: string, fetchImpl: typeof fetch = fetch): Promise<OidcCallbackCompleteResult> {
  const response = await fetchImpl(authEndpoint("/callback"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...applicationHeaders() },
    body: JSON.stringify({ code, state }),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  const result = await parseJson<{ redirect_path: string; user: MicroLabCurrentUser }>(response);
  return { redirectPath: result.redirect_path };
}

export async function bootstrapMicroLabAppSession(fetchImpl: typeof fetch = fetch) {
  const bootstrapResponse = await fetchImpl(authEndpoint("/bootstrap"), {
    method: "POST",
    credentials: "include",
    headers: applicationHeaders(),
  });
  if (!bootstrapResponse.ok) {
    throw createApiErrorFromResponse(bootstrapResponse, await readApiErrorDetail(bootstrapResponse));
  }
  return await parseJson<MicroLabCurrentUser | null>(bootstrapResponse);
}

export async function bootstrapSession(fetchImpl: typeof fetch = fetch) {
  try {
    return await bootstrapMicroLabAppSession(fetchImpl);
  } catch (error) {
    clearSession();
    throw error;
  }
}

export async function logout(fetchImpl: typeof fetch = fetch) {
  const response = await fetchImpl(authEndpoint("/logout"), {
    method: "POST",
    credentials: "include",
    headers: applicationHeaders(),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  clearSession();
}

export function syncMicroLabAdminInitialState<T extends MicroLabAdminInitialState>(
  state: T | undefined,
  next: {
    currentUser: MicroLabCurrentUser;
    microLabUser: MicroLabCurrentUser | null;
  },
) : T {
  const currentState = requireMicroLabAdminInitialState(state);
  return {
    ...currentState,
    authStatus: "authenticated",
    currentUser: next.currentUser,
    microLabUser: next.microLabUser,
  } as T;
}

export function clearMicroLabAdminInitialState<T extends MicroLabAdminInitialState>(
  state: T | undefined,
  overrides: {
    authStatus?: MicroLabAdminAuthStatus;
    currentUser?: MicroLabCurrentUser | null;
  } = {},
) : T {
  const currentState = requireMicroLabAdminInitialState(state);
  return {
    ...currentState,
    authStatus: overrides.authStatus ?? "no_session",
    currentUser: overrides.currentUser ?? null,
    microLabUser: null,
  } as T;
}

function requireMicroLabAdminInitialState<T extends MicroLabAdminInitialState>(
  state: T | undefined,
): T {
  if (typeof state === "undefined") {
    throw new Error("Micro Lab admin initial state is required before auth session mutation");
  }
  return state;
}
